"use client";
import { useFuzzySearchList } from "@nozbe/microfuzz/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { SecondaryButton } from "ui/components";
import type { Value, ValueType } from "../../_utils/types";
import { DesktopCheckbox } from "./DesktopCheckbox";

type Props = {
  type: ValueType;
  onChange: (newValue: Value, directUpdate?: boolean) => void;
  onValidate: () => void;
  onClear: () => void;
  localValues: Value[];
  selectedValues: Value[];
  values: { label: string; value: string }[];
  placeholder: string;
  disableSort?: boolean;
  disableSearch?: boolean;
  large?: boolean;
  fit?: boolean;
};
export const MultiSelect = ({
  type,
  onChange,
  onValidate,
  onClear,
  localValues,
  selectedValues,
  values,
  placeholder,
  disableSort = false,
  disableSearch = false,
  large,
  fit = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const filteredList = useFuzzySearchList({
    list: values,
    queryText: search,
    key: "label",
    mapResultItem: ({ item }) => item,
  });

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (ref.current && !ref.current.contains(event.target as Node) && isOpen) {
        event.stopPropagation();
        setIsOpen(false);
        setSearch("");
        onValidate();
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen, onValidate]);

  const firstSelectedValue = selectedValues[0];
  const option = values.find((v) => v.value === firstSelectedValue?.value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={clsx(
          "h-8 rounded-[28px] border py-1.5 pr-2 text-sm font-medium flex items-center space-x-1",
          fit
            ? {
                "bg-white hover:bg-fit-4 border-fit-2 text-tx-1 stroke-tx-1": selectedValues.length === 0,
                "bg-fit-4 border-fit-3 text-fit-3 stroke-fit-3": selectedValues.length !== 0,
              }
            : {
                "bg-white hover:bg-[#FBF7F4] border-[#F4EAE1] text-primary stroke-primary": selectedValues.length === 0,
                "bg-[#FBF7F4] border-pr-1 text-pr-1 stroke-pr-1": selectedValues.length !== 0,
              },
          {
            "pl-[0.22rem]": firstSelectedValue,
            "pl-3": !firstSelectedValue,
          },
        )}
      >
        {firstSelectedValue ? (
          <>
            <p
              className={clsx("flex items-center text-white rounded-[18px] py-0.5 pl-1 pr-1.5", {
                "bg-fit-3": fit,
                "bg-pr-1": !fit,
              })}
              onClick={(e) => {
                e.stopPropagation();
                onChange(firstSelectedValue, true);
              }}
            >
              <svg
                className="rotate-45"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path d="M8 2V14" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 8L2 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{option?.label || firstSelectedValue.value}</span>
            </p>
            {selectedValues.length > 1 && <span>{`+${selectedValues.length - 1}`}</span>}
          </>
        ) : (
          <span> {placeholder}</span>
        )}
        <svg
          className={clsx("transition-transform", { "rotate-180": !isOpen })}
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M12 10L8.17678 6.17678C8.07915 6.07915 7.92085 6.07915 7.82322 6.17678L4 10"
            stroke="inherit"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {isOpen && (
        <div
          ref={ref}
          className={clsx(
            "absolute top-full mt-1 left-0 bg-white border rounded-md z-10 flex flex-col space-y-2 shadow-xs p-3",
            fit ? "border-fit-2" : "border-[#F4EAE1]",
            {
              "w-60": !large,
              "w-96": large,
            },
          )}
        >
          {!disableSearch && (
            <input
              className="p-3 w-full focus:outline-hidden text-sm leading-4 border bg-[#FCF6F0] border-[#F4EAE1] rounded-md focus:bg-white placeholder:text-[#C1B1AC]"
              placeholder="Rechercher"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          )}
          <div
            className={clsx("overflow-y-auto grow max-h-60 grid gap-0.5", {
              "grid-cols-1": !large,
              "grid-cols-2": large,
            })}
          >
            {filteredList
              .sort((a, b) => (disableSort ? 0 : a.label.localeCompare(b.label)))
              .map((value) => (
                <DesktopCheckbox
                  key={value.value}
                  label={value.label}
                  checked={localValues.some((lt) => lt.value === value.value)}
                  onChange={() =>
                    onChange({
                      type,
                      value: value.value,
                    })
                  }
                />
              ))}
          </div>
          <div
            className={clsx({
              "grid grid-cols-2 gap-1": firstSelectedValue,
            })}
          >
            {firstSelectedValue && (
              <SecondaryButton
                className="w-full"
                size="sm"
                direction="up"
                onClick={() => {
                  setIsOpen(false);
                  setSearch("");
                  onClear();
                }}
              >
                Effacer
              </SecondaryButton>
            )}
            <SecondaryButton
              className="w-full"
              size="sm"
              direction="up"
              onClick={() => {
                setIsOpen(false);
                setSearch("");
                onValidate();
              }}
            >
              Valider
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  );
};
